import { useAuthState } from '@kaa/auth/common';
import { CommonRoutes } from '@kaa/common/constants';
import { RouteActive } from '@kaa/common/types';
import { useAsyncCallback } from '@kaa/common/utils';
import { i18nKeys } from '@kaa/i18n/customers/keys';
import {
  Icon,
  SwFunctionalHeader,
  SwIcon,
  SwTabs,
} from '@kaa/ui-flanders/components';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { dataTest } from '../../datatest/keys';
import { getPath, getRoute, Routes } from '../../routes';
import { AuthContext } from '../../types';
import { useDispatchUpdateSelectedCustomer } from '../../utils';
import {
  EventAction,
  sendMainNavigationEvent,
} from '../../utils/google-analytics';
import './VlFunctionalHeader.style.scss';
import {
  getTabs,
  hasAccessToAccountPreferences,
} from './VlFunctionalHeader.utils';
import { VlFunctionalHeaderActions } from './VlFunctionalHeaderActions';

const ROUTES_TO_SHOW_INFORMATION_LINK = [Routes.ROOT, Routes.LANDING];

type VlFunctionalHeaderProps = {
  activeRoute?: RouteActive<Routes> | null;
};

export const VlFunctionalHeader = ({
  activeRoute,
}: VlFunctionalHeaderProps) => {
  const { customers, selectedCustomerId, logout } = useAuthState<AuthContext>();
  const dispatchUpdateSelectedCustomer = useDispatchUpdateSelectedCustomer();
  const [{ loading }, logoutFunc] = useAsyncCallback(logout);
  const { t } = useTranslation();

  const selectedCustomer = customers?.find(
    ({ id }) => id.toString() === selectedCustomerId,
  );

  const tabs = useMemo(() => {
    if (!activeRoute) return undefined;

    const options = {
      account: {
        showPreferences: hasAccessToAccountPreferences(selectedCustomer),
      },
    };

    return getTabs({ activeRoute: activeRoute.route.id, options, t });
  }, [activeRoute, selectedCustomer, t]);

  if (!tabs || !activeRoute) {
    return null;
  }

  const showBackToInformationLink = ROUTES_TO_SHOW_INFORMATION_LINK.includes(
    activeRoute.route.id as CommonRoutes,
  );

  return (
    <SwFunctionalHeader
      tagName="div"
      title={t(i18nKeys.general.appTitle)}
      actions={
        selectedCustomerId && (
          <VlFunctionalHeaderActions
            customers={customers}
            selectedCustomerId={selectedCustomerId}
            logout={logoutFunc}
            loadingLogout={loading}
            dispatchUpdateSelectedCustomer={dispatchUpdateSelectedCustomer}
          />
        )
      }
      modBorder
      subActions={
        tabs && (
          <SwTabs
            tabs={[
              ...(showBackToInformationLink
                ? [
                    {
                      tagName: 'a',
                      href: t(
                        i18nKeys.vl.general.serviceVouchers.publicWebsite.url,
                      ),
                      className: 'vl-link vl-u-display-inline-flex',
                      header: (
                        <>
                          <SwIcon
                            className="vl-link__icon"
                            icon={Icon.ARROW_LEFT}
                          />
                          {t(
                            i18nKeys.vl.header.funcHeader.cta.backToInfoWebsite
                              .title,
                          )}
                        </>
                      ),
                      'data-testid':
                        dataTest.header.backToPublicServiceVouchersLogin,
                      uid: 'showBackToInformationLink',
                      onClick: () => {
                        sendMainNavigationEvent(
                          EventAction.BACK,
                          t(
                            i18nKeys.vl.general.serviceVouchers.publicWebsite
                              .url,
                          ),
                          t(
                            i18nKeys.vl.header.funcHeader.cta.backToInfoWebsite
                              .title,
                          ),
                        );
                      },
                    },
                  ]
                : []),
              ...tabs.map(({ id, active, ...rest }) => ({
                ...rest,
                id,
                active,
                to: getPath(id),
                isActive: active || id === activeRoute.route.id,
                onClick: () => {
                  const route = getRoute(id);
                  if (route.parent) {
                    sendMainNavigationEvent(route.title, route.path);
                  } else {
                    sendMainNavigationEvent(
                      activeRoute.route.id === Routes.DASHBOARD
                        ? EventAction.CLICK
                        : EventAction.BACK,
                      route.path,
                    );
                  }
                },
              })),
            ]}
          />
        )
      }
    />
  );
};
